/**
 * Admin merchants LIST hook, carved out of the admin `features/merchants/hooks.ts`.
 *
 * Shared because ≥2 apps need the admin merchant search: the admin MerchantsPage
 * and the comerciante owner/KDS store pickers (admin-support path). The rest of
 * the merchants feature (detail, orders, reviews, mutations) stays in admin and
 * re-imports `useMerchants`/`fetchAuthorized` from here.
 *
 * `GET /api/v1/admin/merchants` exists on the backend but isn't in the generated
 * OpenAPI surface yet, so it goes through a direct authorized fetch.
 * TODO: drop the manual fetch once the OpenAPI is updated.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError } from './errors';
import { getAccessToken } from '../auth/tokens';
import type { components } from './generated/api';

type MerchantStatusEnum = components['schemas']['MerchantStatusEnum'];
type PageResponseMerchant = components['schemas']['PageResponseMerchantResponse'];

export interface MerchantsListFilters {
  page: number;
  size: number;
  status?: MerchantStatusEnum | undefined;
  categoryId?: string | undefined;
  /** Free-text search executed client-side over the current page. */
  search?: string | undefined;
}

const baseUrl = import.meta.env.VITE_API_BASE ?? '';

/**
 * Direct authorized GET fallback for admin endpoints not yet in the typed client.
 * Shared with the admin merchant-detail fetch.
 */
export const fetchAuthorized = async <T,>(path: string): Promise<T> => {
  const token = getAccessToken();
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    throw new ApiError(response.status, {}, `HTTP ${response.status}`);
  }
  return (await response.json()) as T;
};

/**
 * Direct fetch for `GET /api/v1/admin/merchants`. Returns the same
 * `PageResponseMerchantResponse` shape Spring serializes for the typed
 * counterpart endpoints.
 */
export const fetchAdminMerchantsList = (
  filters: MerchantsListFilters,
): Promise<PageResponseMerchant> => {
  const params = new URLSearchParams();
  params.set('page', String(filters.page));
  params.set('size', String(filters.size));
  if (filters.status) params.set('status', filters.status);
  if (filters.categoryId) params.set('categoryId', filters.categoryId);
  return fetchAuthorized<PageResponseMerchant>(
    `/api/v1/admin/merchants?${params.toString()}`,
  );
};

export const useMerchants = (
  filters: MerchantsListFilters,
): UseQueryResult<PageResponseMerchant> =>
  useQuery({
    // Key kept identical to the admin feature's `merchantsKeys.list(filters)`.
    queryKey: ['merchants', 'list', filters],
    queryFn: () => fetchAdminMerchantsList(filters),
  });
